import { getStyle, resolveText, wrapText } from "./studioModel";
import { groupPath } from "./studioGroups";
import { textWidth } from "./studioTextFit";

export const HEADING_GAP = 8;
export const HEADING_VINE_WIDTH = 36;
const visible = (n) => !n.hidden && n.opacity !== 0;
const round = (n) => Math.round(n * 10000) / 10000;

// Only recognize the imported paired flourishes, never corner ornaments or
// arbitrary artwork. Once linked, identity replaces proximity-based discovery.
export function connectHeadingWinglets(doc) {
  return { ...doc, pages: doc.pages.map((page) => {
    const elements = page.elements.map((n) => ({ ...n }));
    const used = new Set(elements.filter((n) => n.headingWinglet).map((n) => n.id));
    for (const heading of elements.filter((n) => n.type === "text" && n.role === "section" && visible(n) && !n.locked)) {
      if (elements.some((n) => n.headingWinglet && n.attachedTo === heading.id)) continue;
      const cx = heading.x + heading.w / 2;
      const candidates = elements.filter((n) => n.type === "image" && n.name === "Original flourish" && visible(n) && !n.locked && !used.has(n.id) && !n.attachedTo && Math.abs(n.y + n.h / 2 - heading.y - heading.h / 2) < 24);
      const side = (left) => candidates.filter((n) => (n.x + n.w / 2 < cx) === left).sort((a,b) => Math.abs(a.y-heading.y)-Math.abs(b.y-heading.y))[0];
      const left = side(true), right = side(false);
      if (!left || !right) continue;
      const members = [heading, left, right];
      const paths = members.map(groupPath);
      const prefix = paths[0].filter((g, i) => paths.every((p) => p[i]?.id === g.id));
      const group = { id: `heading-${heading.id}`, name: `${String(heading.text || heading.name).slice(0, 85)} · heading` };
      members.forEach((n) => { n.groupPath = [...prefix, group]; n.groupId = group.id; });
      [left, right].forEach((n, i) => {
        used.add(n.id);
        n.attachedTo = heading.id;
        n.headingWinglet = { side: i ? "right" : "left", gap: HEADING_GAP };
      });
    }
    return { ...page, elements };
  }) };
}

export function alignHeadingWinglets(doc, content = {}) {
  return { ...doc, pages: doc.pages.map((page) => {
    const elements = page.elements.map((n) => ({ ...n }));
    for (const heading of elements.filter((n) => n.type === "text" && n.role === "section")) {
      const wings = elements.filter((n) => n.headingWinglet && n.attachedTo === heading.id);
      if (!wings.length || heading.locked || wings.some((n) => n.locked)) continue;
      const s = getStyle(doc, heading), text = String(resolveText(heading, content));
      const lines = wrapText(s.textTransform === "uppercase" ? text.toUpperCase() : text, s, heading.w, (t) => textWidth(t, s));
      const original = heading.originalLayout;
      const useOriginal = original && text === original.text && heading.w === original.w && heading.h === original.h && Object.entries(original.style).every(([k,v]) => s[k] === v);
      const runs = useOriginal ? original.runs : lines.map((line, i) => {
        const width = textWidth(line, s);
        return { text: line, x: s.align === "center" ? (heading.w-width)/2 : s.align === "right" ? heading.w-width : 0, y: s.fontSize*.86+i*s.fontSize*s.lineHeight };
      });
      const left = Math.min(...runs.map((r) => r.x));
      const right = Math.max(...runs.map((r) => r.x + textWidth(r.text, s)));
      const top = Math.min(...runs.map((r) => r.y)) - s.fontSize*.72;
      const bottom = Math.max(...runs.map((r) => r.y));
      const cx = heading.x+heading.w/2, cy = heading.y+heading.h/2;
      const angle = (heading.rotation || 0)*Math.PI/180;
      wings.forEach((wing) => {
        const gap = Number.isFinite(wing.headingWinglet.gap) ? wing.headingWinglet.gap : HEADING_GAP;
        const w = wing.w > 0 ? wing.w : HEADING_VINE_WIDTH, h = wing.h > 0 ? wing.h : w * 78 / 285;
        const x = heading.x+(wing.headingWinglet.side === "left" ? left-gap-w : right+gap);
        const y = heading.y+(top+bottom)/2-h/2+(wing.headingWinglet.offsetY || 0);
        const dx = x+w/2-cx, dy = y+h/2-cy;
        Object.assign(wing, {x:round(cx+dx*Math.cos(angle)-dy*Math.sin(angle)-w/2),y:round(cy+dx*Math.sin(angle)+dy*Math.cos(angle)-h/2),w,h,rotation:(heading.rotation || 0)+(wing.headingWinglet.rotationOffset || 0)});
        delete wing.headingRuleLength;
      });
    }
    return { ...page, elements };
  }) };
}

// Convert deliberate ornament transforms into offsets in the heading's local
// coordinates. Future text changes retain these choices instead of snapping back.
export function retainHeadingAdjustments(before, after, content = {}) {
  const old = new Map(before.pages.flatMap((p) => p.elements).map((n) => [n.id, n]));
  const expected = new Map(alignHeadingWinglets(after, content).pages.flatMap((p) => p.elements).map((n) => [n.id, n]));
  return { ...after, pages: after.pages.map((page) => ({ ...page, elements: page.elements.map((n) => {
    const previous = old.get(n.id), heading = page.elements.find((h) => h.id === n.attachedTo), priorHeading = old.get(n.attachedTo);
    if (!n.headingWinglet || !previous || !heading || !priorHeading || n.locked) return n;
    const meta = { ...n.headingWinglet };
    if (n.src !== previous.src) meta.customArtwork = true;
    const headingMoved = ["x", "y", "w", "h", "rotation"].some((k) => heading[k] !== priorHeading[k]);
    const moved = ["x", "y", "w", "h"].some((k) => n[k] !== previous[k]);
    if (!headingMoved && moved) {
      const target = expected.get(n.id), angle = (heading.rotation || 0) * Math.PI / 180;
      const dx = n.x - target.x, dy = n.y - target.y;
      meta.gap = round((meta.gap ?? HEADING_GAP) + (meta.side === "left" ? -1 : 1) * (dx * Math.cos(angle) + dy * Math.sin(angle)));
      meta.offsetY = round((meta.offsetY || 0) - dx * Math.sin(angle) + dy * Math.cos(angle));
    }
    if (!headingMoved && n.rotation !== previous.rotation) meta.rotationOffset = n.rotation - (heading.rotation || 0);
    return { ...n, headingWinglet: meta };
  }) })) };
}
